import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import {
  INominationLimitUploadResponse,
  INominationLimitOptions,
  INominationLimitQuery,
} from '@/app/core/interfaces/nomination-limit.interface';
import { AppSettings } from '@/app/core/utils/app.settings';
import { HttpService } from '@/app/core/services/http/http.service';

@Injectable({
  providedIn: 'root',
})
export class NominationLimitService {
  nominationLimitData: any[] = [];
  options: INominationLimitOptions | null = null;

  constructor(
    private http: HttpService,
    private httpClient: HttpClient,
    private appSetting: AppSettings
  ) {}

  /**
   * Obtiene las opciones para combos (puntos + transportadores)
   */
  getNominationLimitOptions(): Observable<INominationLimitOptions> {
    const url: string = `${this.appSetting.nominationLimit.urls.base}/options`;
    return this.httpClient.get<INominationLimitOptions>(url);
  }

  /**
   * Descarga el template para carga de archivos
   */
  async downloadTemplate(): Promise<Blob> {
    const url: string = `${this.appSetting.nominationLimit.urls.base}/template`;
    return await firstValueFrom(
      this.httpClient.get(url, { responseType: 'blob' })
    );
  }

  /**
   * Detecta si el error está relacionado con estructura/columnas faltantes del archivo
   */
  private isStructureError(error: any): boolean {
    const errorMessage = (
      error?.error?.message ||
      error?.message ||
      (typeof error?.error === 'string' ? error.error : '') ||
      JSON.stringify(error?.error || {}).toLowerCase() ||
      ''
    ).toLowerCase();

    const structureKeywords = [
      'columna',
      'columnas',
      'estructura',
      'estructura requerida',
      'missing',
      'required',
      'faltan',
      'no cumple',
      'invalid structure',
      'missing column',
      'column missing',
      'required column',
      'campo requerido',
      'campo faltante',
      'invalid format',
      'formato inválido',
      'invalid file',
      'archivo inválido',
    ];

    const hasStructureKeyword = structureKeywords.some(keyword =>
      errorMessage.includes(keyword)
    );

    if (hasStructureKeyword) {
      return true;
    }

    const isBadRequest =
      error?.status === 400 || error?.error?.status === 400;
    if (isBadRequest && (!errorMessage || errorMessage.includes('bad request'))) {
      return true;
    }

    return false;
  }

  /**
   * Sube un archivo de límite de nominación
   */
  async uploadNominationLimit(
    file: File
  ): Promise<INominationLimitUploadResponse> {
    const url: string = this.appSetting.nominationLimit.urls.upload;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await firstValueFrom(
        this.httpClient.post<INominationLimitUploadResponse>(url, formData)
      );

      // Limpiar datos en memoria para forzar una nueva consulta después de cargar
      this.nominationLimitData = [];

      return {
        success: true,
        message: 'Archivo subido correctamente',
        data: data.data || [],
      };
    } catch (err: any) {
      // Extraer errores detallados del backend si existen
      const backendErrors = err.error?.errors || [];
      const backendMessage =
        err.error?.message || err.message || 'Error al subir el archivo';

      // Si hay errores detallados, retornarlos junto con el mensaje general
      if (backendErrors.length > 0) {
        return {
          success: false,
          message: backendMessage,
          errors: backendErrors.map((error: any) => ({
            row: error.row || 0,
            message: error.message || error,
          })),
        };
      }

      // Si el error está relacionado con estructura/columnas faltantes, mostrar mensaje específico
      if (this.isStructureError(err)) {
        return {
          success: false,
          message: 'El archivo no cumple con la estructura requerida',
        };
      }

      return {
        success: false,
        message: backendMessage,
      };
    }
  }

  /**
   * Obtiene límites de nominación por periodos
   */
  getNominationLimitByPeriodos(query?: INominationLimitQuery): Observable<any[]> {
    const url: string = `${this.appSetting.nominationLimit.urls.base}/periodos`;

    // Construir query params
    const params = new URLSearchParams();
    if (query?.fechaInicio) params.append('fechaInicio', query.fechaInicio);
    if (query?.fechaFin) params.append('fechaFin', query.fechaFin);
    if (query?.puntoUuid) params.append('puntoUuid', query.puntoUuid);
    if (query?.codigoSIU) params.append('codigoSIU', query.codigoSIU);

    const queryString = params.toString();
    const queryParams = queryString ? `?${queryString}` : '';

    return this.httpClient.get<any[]>(`${url}${queryParams}`);
  }

  /**
   * Confirma valores de límite de nominación
   * @param periodo Periodo en formato 'YYYY-MM-01' para confirmar
   */
  async confirmNominationLimit(
    periodo: string
  ): Promise<{ success: boolean; message?: string }> {
    const url: string = `${this.appSetting.nominationLimit.urls.base}/confirm`;

    try {
      await this.http.postService(url, { periodo });

      return {
        success: true,
        message: 'Valores confirmados correctamente',
      };
    } catch (err: any) {
      let errorMessage = 'Error al confirmar valores';

      if (err?.error) {
        errorMessage = err.error.details || err.error.message || errorMessage;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  /**
   * Obtiene los últimos límites de nominación por punto (histórico)
   * @param puntoUuid UUID del punto de salida
   */
  getNominationLimitByPunto(puntoUuid: string): Observable<any[]> {
    const url: string = `${this.appSetting.nominationLimit.urls.base}/punto/${puntoUuid}`;
    return this.httpClient.get<any[]>(url);
  }

  /**
   * Limpia el estado local (útil para forzar recarga)
   */
  clearLocalData(): void {
    this.nominationLimitData = [];
    this.options = null;
  }
}

